import { toaster } from '@/components/ui/toaster';
import api from '@/libs/axiosInstance';
import type { AgentSettings, AgentSlug, ApiResponse } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

/**
 * updates the list of enabled agents for a brand.
 *
 * V1 only mutates `enabledAgents`. Budget + max-tokens stay admin-controlled.
 */
export const useUpdateAgentSettingsAction = (brandId: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<ApiResponse<AgentSettings>, Error, { enabledAgents: AgentSlug[] }>({
    mutationFn: async (payload) => {
      const response = await api.put(`/dashboard/brands/${brandId}/agent-settings`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agentSettings', brandId] });
      toaster.success({
        title: t('settings.agents.updateSuccessTitle'),
        description: t('settings.agents.updateSuccessDescription'),
      });
    },
    onError: () => {
      toaster.error({
        title: t('common.error'),
        description: t('settings.agents.updateErrorDescription'),
      });
    },
  });
};
